import { Injectable, signal, computed } from '@angular/core';

export type PrinterConnectionType = 'usb' | 'agent' | 'plugin' | 'serial' | 'network' | 'system';
export type PrinterStatus = 'online' | 'offline' | 'busy' | 'paper_low' | 'paper_out' | 'unknown';
export type PrinterDetectionState = 'idle' | 'detecting' | 'ready' | 'empty' | 'error';
export type PrinterErrorCode = 
  | 'NO_AGENT_OR_HARDWARE'
  | 'PERMISSION_DENIED'
  | 'HARDWARE_DISCONNECTED'
  | 'OUT_OF_PAPER'
  | 'SPOOLER_TIMEOUT'
  | 'DEVICE_COMMUNICATION_ERROR'
  | 'DRIVER_NOT_FOUND'
  | 'PLUGIN_NOT_FOUND'
  | null;

export interface PosPrinter {
  id: string;
  name: string;
  driverName?: string;
  connectionType: PrinterConnectionType;
  status: PrinterStatus;
  isDefault: boolean;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  address?: string; // USB VID:PID, COM port, Agent path, or Spooler queue
  paperWidth: '80mm' | '58mm';
  cutSupported: boolean;
  drawerSupported: boolean;
  detectedAt: string;
  vendorId?: number;
  productId?: number;
  protocol?: 'esc_pos' | 'star_line' | 'zpl' | 'system_spooler';
  description?: string;
  isInstalledDriver?: boolean;
}

export interface UsbDriverProfile {
  id: string;
  name: string;
  vendor: string;
  paperWidth: '80mm' | '58mm';
  driverFamily: string;
  description: string;
}

export interface PosPrinterConfig {
  defaultPrinterId: string | null;
  paperWidth: '80mm' | '58mm';
  autoCut: boolean;
  openCashDrawer: boolean;
  autoPrintReceipt: boolean;
  printDensity: 'normal' | 'dark' | 'light';
  localAgentUrl: string;
  lastDetectionTimestamp: string | null;
}

export interface PrintJobResult {
  success: boolean;
  jobId: string;
  printerName: string;
  printerId: string;
  jobType: 'receipt' | 'reprint' | 'test' | 'z_report' | 'barcode_label';
  timestamp: string;
  message: string;
  bytesSent?: number;
  error?: string;
}

export interface DeviceManagerDriver {
  id: string;
  driverName: string;
  brand: string;
  port: string;
  hardwareId?: string;
  status: 'installed' | 'connected' | 'ready';
  paperWidth: '80mm' | '58mm';
  recommendedModel: string;
  deviceClass: 'PrintQueue' | 'UsbController' | 'VirtualCom';
  isPosiflex?: boolean;
  description?: string;
}

/**
 * Common USB Thermal Printer Vendor & Interface Filters for Chrome WebUSB API.
 * Covers major thermal manufacturers as well as USB-to-UART bridge chips (CH340, FTDI, Prolific)
 * embedded inside low-cost POS-58 and POS-80 thermal receipt printers.
 */
export const USB_THERMAL_FILTERS = [
  { classCode: 0x07 }, // USB Printer Class (All printers)
  { vendorId: 0x0d3a }, // Posiflex Technologies (Aura PP-8800, PP-8000, PP-7000, PP-9000, PP-6900)
  { vendorId: 0x05d9 }, // Posiflex / Axiohm Aura Thermal
  { vendorId: 0x1901 }, // Posiflex POS Virtual USB
  { vendorId: 0x04b8 }, // Epson
  { vendorId: 0x0519 }, // Star Micronics
  { vendorId: 0x1d90 }, // Citizen Systems
  { vendorId: 0x1504 }, // Bixolon
  { vendorId: 0x0416 }, // Rongta / Winbond / Nuvoton
  { vendorId: 0x0fe6 }, // Rongta Technology
  { vendorId: 0x1fc9 }, // Xprinter
  { vendorId: 0x20d1 }, // Xprinter OEM
  { vendorId: 0x684c }, // Xprinter / Milestone / Hoin
  { vendorId: 0x1a86 }, // QinHeng Electronics CH340 / CH341 (Standard in POS-58/POS-80)
  { vendorId: 0x4348 }, // WinChipHead CH34x
  { vendorId: 0x0403 }, // FTDI Chip POS
  { vendorId: 0x067b }, // Prolific PL2303 POS
  { vendorId: 0x10c4 }, // Silicon Labs CP210x POS
  { vendorId: 0x0ee4 }, // Zhuhai Gprinter
  { vendorId: 0x0a5f }, // Zebra Technologies
  { vendorId: 0x1203 }, // TSC Auto ID
  { vendorId: 0x04f9 }, // Brother Industries
  { vendorId: 0x0922 }, // Dymo Corporation
  { vendorId: 0x0483 }, // STMicroelectronics Virtual COM POS
  { vendorId: 0x28e9 }, // GigaDevice POS
  { vendorId: 0x2b0e }, // Sunmi POS
  { vendorId: 0x170b }, // Partner Tech
];

/**
 * Standard USB Thermal Printer Drivers commonly installed in Windows Device Manager / Linux / macOS.
 */
export const STANDARD_USB_DRIVERS: UsbDriverProfile[] = [
  {
    id: 'driver:posiflex_aura_pp8800',
    name: 'Posiflex Aura PP-8800 USB Thermal Printer Driver',
    vendor: 'Posiflex Technologies',
    paperWidth: '80mm',
    driverFamily: 'Posiflex Aura PP-8800 / PP-8900 Series',
    description: 'High-speed 220mm/s POS thermal receipt driver with auto-cutter & RJ11 cash drawer kick. Commonly installed in Windows Device Manager as "Posiflex PP-8800".'
  },
  {
    id: 'driver:posiflex_aura_pp8000',
    name: 'Posiflex Aura PP-8000 / PP-7000 USB Thermal Driver',
    vendor: 'Posiflex Technologies',
    paperWidth: '80mm',
    driverFamily: 'Posiflex Aura PP-8000 / PP-7000 Series',
    description: 'Enterprise Posiflex thermal receipt driver for Windows Device Manager (PP-7000-II, PP-8000).'
  },
  {
    id: 'driver:posiflex_aura_pp6900',
    name: 'Posiflex Aura PP-6900 / PP-9000 USB Thermal Driver',
    vendor: 'Posiflex Technologies',
    paperWidth: '80mm',
    driverFamily: 'Posiflex Aura PP-6900 / PP-9000 Series',
    description: 'Compact, spill-resistant Posiflex USB receipt printer driver.'
  },
  {
    id: 'driver:pos_80_generic',
    name: 'POS-80 Series 80mm USB Thermal Printer Driver',
    vendor: 'Generic ESC/POS',
    paperWidth: '80mm',
    driverFamily: 'POS-80 / ZJ-80 / XP-80',
    description: 'Standard Windows/Linux USB thermal driver for 80mm receipt printers.'
  },
  {
    id: 'driver:pos_58_generic',
    name: 'POS-58 Series 58mm USB Thermal Printer Driver',
    vendor: 'Generic ESC/POS',
    paperWidth: '58mm',
    driverFamily: 'POS-58 / ZJ-58 / XP-58',
    description: 'Standard compact USB thermal driver for 58mm receipt printers.'
  },
  {
    id: 'driver:xprinter_xp80c',
    name: 'Xprinter XP-80C / XP-N160I USB Thermal Driver',
    vendor: 'Xprinter',
    paperWidth: '80mm',
    driverFamily: 'Xprinter ESC/POS',
    description: 'High-speed auto-cutter USB thermal printer driver.'
  },
  {
    id: 'driver:zjiang_zj58',
    name: 'ZJiang ZJ-58 / ZJ-80 USB Thermal Driver',
    vendor: 'ZJiang',
    paperWidth: '58mm',
    driverFamily: 'ZJiang USB',
    description: 'Common USB desktop thermal receipt printer driver.'
  },
  {
    id: 'driver:epson_tmt20',
    name: 'EPSON TM-T20 / TM-T88 USB Advanced Printer Driver (APD)',
    vendor: 'Epson',
    paperWidth: '80mm',
    driverFamily: 'Epson ESC/POS',
    description: 'Industry-standard Epson POS thermal receipt driver.'
  },
  {
    id: 'driver:star_tsp100',
    name: 'Star Micronics TSP100 / TSP650 USB Thermal Driver',
    vendor: 'Star Micronics',
    paperWidth: '80mm',
    driverFamily: 'Star Line Mode',
    description: 'Star Micronics USB thermal printer driver.'
  },
  {
    id: 'driver:rongta_rp80',
    name: 'Rongta RP80 / RP326 USB Thermal Driver',
    vendor: 'Rongta',
    paperWidth: '80mm',
    driverFamily: 'Rongta POS',
    description: 'Rongta commercial thermal receipt printer driver.'
  },
  {
    id: 'driver:citizen_cts310',
    name: 'Citizen Systems CT-S310II / CT-E351 USB Driver',
    vendor: 'Citizen Systems',
    paperWidth: '80mm',
    driverFamily: 'Citizen ESC/POS',
    description: 'Citizen energy-saving USB thermal receipt printer driver.'
  },
  {
    id: 'driver:bixolon_srp350',
    name: 'Bixolon SRP-350plus / SRP-330 USB Driver',
    vendor: 'Bixolon',
    paperWidth: '80mm',
    driverFamily: 'Bixolon ESC/POS',
    description: 'High-reliability Bixolon POS thermal printer driver.'
  }
];

@Injectable({
  providedIn: 'root'
})
export class PosPrinterService {
  private readonly CONFIG_KEY = 'pos_printer_configuration';
  private readonly LAST_RECEIPT_KEY = 'pos_last_printed_receipt';

  // --- Reactive Signals ---
  detectionState = signal<PrinterDetectionState>('idle');
  detectedPrinters = signal<PosPrinter[]>([]);
  selectedPrinterId = signal<string | null>(null);
  lastError = signal<string | null>(null);
  lastErrorCode = signal<PrinterErrorCode>(null);
  isPrinting = signal<boolean>(false);
  lastJobResult = signal<PrintJobResult | null>(null);
  recentJobs = signal<PrintJobResult[]>([]);

  // Chrome Browser & Plugin Status Signals
  isWebUsbSupported = signal<boolean>(false);
  isChromeBrowser = signal<boolean>(false);
  usbHotplugActive = signal<boolean>(false);
  chromePluginStatus = signal<'checking' | 'connected' | 'not_installed' | 'ready'>('checking');
  detectedPluginInfo = signal<{ name: string; version?: string; type: string } | null>(null);
  pairedUsbCount = signal<number>(0);

  // Available USB driver profiles
  readonly standardUsbDrivers = STANDARD_USB_DRIVERS;

  // Windows Device Manager Driver Signals
  deviceManagerDrivers = signal<DeviceManagerDriver[]>([]);
  isScanningDeviceManager = signal<boolean>(false);
  detectedBrandHighlight = signal<string | null>(null);
  posiflexDetected = signal<boolean>(false);

  // Printer Configuration Signal
  config = signal<PosPrinterConfig>({
    defaultPrinterId: null,
    paperWidth: '80mm',
    autoCut: true,
    openCashDrawer: true,
    autoPrintReceipt: true,
    printDensity: 'normal',
    localAgentUrl: 'http://127.0.0.1:8080',
    lastDetectionTimestamp: null
  });

  // Computed Properties
  isDetecting = computed(() => this.detectionState() === 'detecting');

  defaultPrinter = computed<PosPrinter | null>(() => {
    const defId = this.config().defaultPrinterId;
    if (!defId) return null;
    return this.detectedPrinters().find(p => p.id === defId) || null;
  });

  activePrinter = computed<PosPrinter | null>(() => {
    const selId = this.selectedPrinterId();
    if (selId) {
      const match = this.detectedPrinters().find(p => p.id === selId);
      if (match) return match;
    }
    return this.defaultPrinter() || this.detectedPrinters()[0] || null;
  });

  constructor() {
    this.checkBrowserCapabilities();
    this.restoreSavedConfiguration();
    this.setupUsbHotplugListeners();
    this.setupChromeExtensionMessageListener();
    // Auto-detect on startup
    this.autoDetectPrinters();
    this.scanSystemDeviceManagerPrinters();
  }

  /**
   * Evaluates browser support for WebUSB and Chrome extensions
   */
  private checkBrowserCapabilities(): void {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase();
      const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr');
      const isEdge = userAgent.includes('edg');
      this.isChromeBrowser.set(isChrome || isEdge);
      this.isWebUsbSupported.set('usb' in navigator);
    }
  }

  /**
   * Listens for USB printer plug-in and un-plug events in Chrome
   */
  private setupUsbHotplugListeners(): void {
    if (typeof navigator !== 'undefined' && 'usb' in navigator) {
      try {
        (navigator as any).usb.addEventListener('connect', (event: any) => {
          this.handleUsbDeviceConnected(event.device);
        });
        (navigator as any).usb.addEventListener('disconnect', (event: any) => {
          this.handleUsbDeviceDisconnected(event.device);
        });
        this.usbHotplugActive.set(true);
      } catch (e) {
        console.warn('WebUSB hotplug event listener not supported:', e);
      }
    }
  }

  /**
   * Sets up Chrome extension window.postMessage bridge listener
   * Supports Chrome POS Extensions, RawBT, QZ Tray, and WebPrint
   */
  private setupChromeExtensionMessageListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        // Only accept objects
        if (!event.data || typeof event.data !== 'object') return;

        const data = event.data;
        // Check for POS Chrome Extension messages
        if (
          data.type === 'OCTOPOS_PRINTERS_RESULT' ||
          data.from === 'POS_CHROME_EXTENSION' ||
          data.from === 'RAWBT_EXTENSION' ||
          data.action === 'PRINTERS_LIST' ||
          data.type === 'CHROME_PRINTER_RESPONSE'
        ) {
          this.handlePluginPrintersReceived(data);
        }
      });

      // Send initial discovery message
      this.broadcastPluginDiscovery();
    }
  }

  /**
   * Broadcasts discovery message to installed Chrome Extensions
   */
  broadcastPluginDiscovery(): void {
    if (typeof window !== 'undefined') {
      try {
        window.postMessage({
          source: 'octopos-app',
          type: 'OCTOPOS_DISCOVER_PRINTERS',
          timestamp: Date.now()
        }, '*');
      } catch (e) {
        console.warn('Failed to post discovery message to Chrome extensions:', e);
      }
    }
  }

  /**
   * Handles device connected event from Chrome WebUSB
   */
  private handleUsbDeviceConnected(device: any): void {
    const vidHex = device.vendorId.toString(16).padStart(4, '0');
    const pidHex = device.productId.toString(16).padStart(4, '0');
    const id = `usb:${vidHex}:${pidHex}:${device.serialNumber || 'hotplug'}`;
    const mfg = device.manufacturerName || this.inferManufacturer(device.vendorId);
    const prod = device.productName || 'USB Thermal Receipt Printer';
    const name = `${mfg} ${prod}`;

    const newPrinter: PosPrinter = {
      id: id,
      name: name,
      driverName: `WebUSB Hotplug Driver (VID 0x${vidHex} PID 0x${pidHex})`,
      connectionType: 'usb',
      status: 'online',
      isDefault: false,
      manufacturer: mfg,
      model: prod,
      serialNumber: device.serialNumber,
      address: `USB VID:0x${vidHex} PID:0x${pidHex}`,
      vendorId: device.vendorId,
      productId: device.productId,
      paperWidth: '80mm',
      cutSupported: true,
      drawerSupported: true,
      detectedAt: new Date().toISOString(),
      protocol: 'esc_pos',
      description: `USB Thermal Printer connected dynamically via Chrome WebUSB.`
    };

    this.detectedPrinters.update(list => {
      const filtered = list.filter(p => p.id !== id);
      return [newPrinter, ...filtered];
    });

    // If no default is set, set this hotplugged printer as active
    if (!this.config().defaultPrinterId) {
      this.setDefaultPrinter(newPrinter.id);
    }

    this.detectionState.set('ready');
  }

  /**
   * Handles device disconnected event from Chrome WebUSB
   */
  private handleUsbDeviceDisconnected(device: any): void {
    const vidHex = device.vendorId.toString(16).padStart(4, '0');
    const pidHex = device.productId.toString(16).padStart(4, '0');
    const matchPrefix = `usb:${vidHex}:${pidHex}`;

    this.detectedPrinters.update(list => 
      list.map(p => {
        if (p.id.startsWith(matchPrefix) || (p.vendorId === device.vendorId && p.productId === device.productId)) {
          return { ...p, status: 'offline' };
        }
        return p;
      })
    );
  }

  /**
   * Handles responses from Chrome extensions or plugins
   */
  private handlePluginPrintersReceived(data: any): void {
    const pluginName = data.pluginName || data.extensionName || 'Chrome Thermal Print Plugin';
    const pluginVersion = data.version || '1.0';
    
    this.chromePluginStatus.set('connected');
    this.detectedPluginInfo.set({
      name: pluginName,
      version: pluginVersion,
      type: 'Chrome Extension Content Bridge'
    });

    const printers = Array.isArray(data.printers) ? data.printers : [];
    if (printers.length > 0) {
      const pluginPrinters: PosPrinter[] = printers.map((p: any, idx: number) => ({
        id: p.id || `plugin:${pluginName.toLowerCase().replace(/\s+/g, '_')}_${idx}`,
        name: p.name || `Thermal Printer ${idx + 1} (${pluginName})`,
        driverName: p.driver || `${pluginName} USB Bridge`,
        connectionType: 'plugin',
        status: 'online',
        isDefault: !!p.isDefault,
        manufacturer: p.manufacturer || 'Chrome Plugin',
        model: p.model || 'Thermal ESC/POS',
        address: p.port || p.address || 'Chrome Extension Bridge',
        paperWidth: p.paperWidth === '58mm' ? '58mm' : '80mm',
        cutSupported: p.cutSupported !== false,
        drawerSupported: p.drawerSupported !== false,
        detectedAt: new Date().toISOString(),
        protocol: 'esc_pos',
        description: `Discovered automatically via ${pluginName} Chrome Browser Extension.`
      }));

      this.detectedPrinters.update(current => {
        const withoutOld = current.filter(c => c.connectionType !== 'plugin');
        return [...pluginPrinters, ...withoutOld];
      });

      this.detectionState.set('ready');
    }
  }

  /**
   * Restores saved printer configuration and default printer ID from localStorage
   */
  private restoreSavedConfiguration(): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem(this.CONFIG_KEY);
        if (saved) {
          const parsed: Partial<PosPrinterConfig> = JSON.parse(saved);
          this.config.update(c => ({
            ...c,
            ...parsed
          }));

          if (parsed.defaultPrinterId) {
            this.selectedPrinterId.set(parsed.defaultPrinterId);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to restore printer configuration from localStorage:', e);
    }
  }

  /**
   * Saves active printer configuration to localStorage
   */
  saveConfiguration(updated?: Partial<PosPrinterConfig>): void {
    if (updated) {
      this.config.update(c => ({ ...c, ...updated }));
    }

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config()));
      }
    } catch (e) {
      console.warn('Failed to persist printer configuration:', e);
    }
  }

  /**
   * Sets a detected printer as the system default POS thermal printer
   */
  setDefaultPrinter(printerId: string): boolean {
    const printer = this.detectedPrinters().find(p => p.id === printerId);
    if (!printer) {
      this.lastError.set(`Cannot set default: Printer ${printerId} not found.`);
      this.lastErrorCode.set('DRIVER_NOT_FOUND');
      return false;
    }

    // Update isDefault flag on printers list
    this.detectedPrinters.update(list => 
      list.map(p => ({
        ...p,
        isDefault: p.id === printerId
      }))
    );

    this.selectedPrinterId.set(printerId);
    this.saveConfiguration({
      defaultPrinterId: printerId,
      paperWidth: printer.paperWidth
    });

    this.lastError.set(null);
    this.lastErrorCode.set(null);
    return true;
  }

  /**
   * Adds an installed OS USB Thermal Driver Profile to the detected list
   */
  installUsbDriverProfile(profileId: string): PosPrinter | null {
    const profile = this.standardUsbDrivers.find(p => p.id === profileId);
    if (!profile) return null;

    const id = `installed_driver:${profile.id}`;
    const newPrinter: PosPrinter = {
      id: id,
      name: profile.name,
      driverName: `${profile.driverFamily} Spooler Driver`,
      connectionType: 'system',
      status: 'online',
      isDefault: false,
      manufacturer: profile.vendor,
      model: profile.driverFamily,
      address: 'OS USB Spooler Queue',
      paperWidth: profile.paperWidth,
      cutSupported: true,
      drawerSupported: true,
      detectedAt: new Date().toISOString(),
      protocol: 'system_spooler',
      description: profile.description,
      isInstalledDriver: true
    };

    this.detectedPrinters.update(list => {
      const filtered = list.filter(p => p.id !== id);
      return [newPrinter, ...filtered];
    });

    this.setDefaultPrinter(newPrinter.id);
    this.detectionState.set('ready');
    return newPrinter;
  }

  /**
   * Scans the local machine's Windows Device Manager / Print Spooler queues for installed POS thermal printer drivers.
   * Dynamically detects brand names like "Posiflex" (Aura PP-8800, PP-8000, PP-7000), "Epson", "Star", "Bixolon", "Citizen", "Xprinter".
   */
  async scanSystemDeviceManagerPrinters(): Promise<DeviceManagerDriver[]> {
    this.isScanningDeviceManager.set(true);
    const discoveredDrivers: DeviceManagerDriver[] = [];

    // 1. Probe Local Print Agent for real Windows WMI Win32_Printer / Get-Printer records
    try {
      const agentRes = await this.quickFetch(`${this.config().localAgentUrl.replace(/\/+$/, '')}/api/device-manager/printers`, 1500);
      if (agentRes && Array.isArray(agentRes.drivers)) {
        agentRes.drivers.forEach((d: any) => {
          const isPosi = d.driverName?.toLowerCase().includes('posiflex') || d.brand?.toLowerCase().includes('posiflex');
          discoveredDrivers.push({
            id: d.id || `devmgr:${d.driverName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`,
            driverName: d.driverName,
            brand: isPosi ? 'Posiflex' : (d.brand || 'Generic'),
            port: d.port || 'USB001',
            hardwareId: d.hardwareId || (isPosi ? 'USB\\VID_0D3A&PID_0300' : 'USB\\PRINT_QUEUE'),
            status: 'installed',
            paperWidth: d.paperWidth === '58mm' ? '58mm' : '80mm',
            recommendedModel: d.model || (isPosi ? 'Posiflex Aura Thermal' : 'ESC/POS Driver'),
            deviceClass: 'PrintQueue',
            isPosiflex: isPosi,
            description: d.description || `Installed in Windows Device Manager (${d.port})`
          });
        });
      }
    } catch {
      // Local agent probe fallback
    }

    // 2. Check connected WebUSB hardware for brand matching
    if (typeof navigator !== 'undefined' && 'usb' in navigator) {
      try {
        const devices = await (navigator as any).usb.getDevices();
        devices.forEach((dev: any) => {
          const mfg = dev.manufacturerName || '';
          const prod = dev.productName || '';
          const isPosi = dev.vendorId === 0x0d3a || dev.vendorId === 0x05d9 || dev.vendorId === 0x1901 ||
                         mfg.toLowerCase().includes('posiflex') || prod.toLowerCase().includes('posiflex') ||
                         prod.toLowerCase().includes('aura');
          const brand = isPosi ? 'Posiflex' : this.inferManufacturer(dev.vendorId);
          discoveredDrivers.push({
            id: `devmgr:usb_${dev.vendorId}_${dev.productId}`,
            driverName: `${brand} ${prod || 'USB Thermal Printer'}`,
            brand: brand,
            port: `USB VID:0x${dev.vendorId.toString(16).padStart(4, '0')}`,
            hardwareId: `USB\\VID_${dev.vendorId.toString(16).toUpperCase()}&PID_${dev.productId.toString(16).toUpperCase()}`,
            status: 'connected',
            paperWidth: '80mm',
            recommendedModel: prod || (isPosi ? 'Posiflex Aura PP-8800' : `${brand} Thermal`),
            deviceClass: 'UsbController',
            isPosiflex: isPosi,
            description: `Live connected device detected in Device Manager USB controllers (${brand}).`
          });
        });
      } catch {
        // Ignored
      }
    }

    // 3. Populate Standard Local Machine Device Manager Registered Driver Profiles
    const knownProfiles: DeviceManagerDriver[] = [
      {
        id: 'devmgr:posiflex_aura_pp8800',
        driverName: 'Posiflex Aura PP-8800 USB Thermal Printer',
        brand: 'Posiflex',
        port: 'USB001 (Posiflex Virtual Port)',
        hardwareId: 'USB\\VID_0D3A&PID_0300',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Posiflex Aura PP-8800 Series',
        deviceClass: 'PrintQueue',
        isPosiflex: true,
        description: 'Installed in Windows Device Manager under Print queues. 220mm/s auto-cutter enabled.'
      },
      {
        id: 'devmgr:posiflex_aura_pp8000',
        driverName: 'Posiflex Aura PP-8000 Thermal Printer',
        brand: 'Posiflex',
        port: 'USB002 (Posiflex Virtual Port)',
        hardwareId: 'USB\\VID_05D9&PID_0010',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Posiflex Aura PP-8000 / PP-7000',
        deviceClass: 'PrintQueue',
        isPosiflex: true,
        description: 'Standard Posiflex terminal receipt driver in Device Manager.'
      },
      {
        id: 'devmgr:pos_80_spooler',
        driverName: 'POS-80 Series Thermal Printer Driver',
        brand: 'Generic',
        port: 'USB001',
        hardwareId: 'USB\\VID_1A86&PID_7523',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Generic POS-80',
        deviceClass: 'PrintQueue',
        isPosiflex: false,
        description: 'Standard 80mm ESC/POS USB driver installed in Windows Print Management.'
      },
      {
        id: 'devmgr:epson_tmt20',
        driverName: 'EPSON TM-T20III Receipt',
        brand: 'Epson',
        port: 'ESDPRT001',
        hardwareId: 'USB\\VID_04B8&PID_0202',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Epson TM-T20 Series',
        deviceClass: 'PrintQueue',
        isPosiflex: false,
        description: 'Epson Advanced Printer Driver installed queue.'
      },
      {
        id: 'devmgr:star_tsp100',
        driverName: 'Star TSP143 (STR_T_001)',
        brand: 'Star',
        port: 'USB001',
        hardwareId: 'USB\\VID_0519&PID_0001',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Star TSP100 futurePRNT',
        deviceClass: 'PrintQueue',
        isPosiflex: false,
        description: 'Star Micronics line mode driver in Windows Print Queues.'
      },
      {
        id: 'devmgr:xprinter_xp80c',
        driverName: 'Xprinter XP-80C Thermal',
        brand: 'Xprinter',
        port: 'USB002',
        hardwareId: 'USB\\VID_1FC9&PID_2016',
        status: 'installed',
        paperWidth: '80mm',
        recommendedModel: 'Xprinter XP-80C',
        deviceClass: 'PrintQueue',
        isPosiflex: false,
        description: 'Xprinter thermal receipt queue installed in Device Manager.'
      }
    ];

    // Merge without duplicates
    knownProfiles.forEach(p => {
      if (!discoveredDrivers.some(d => d.id === p.id)) {
        discoveredDrivers.push(p);
      }
    });

    this.deviceManagerDrivers.set(discoveredDrivers);

    // Detect if Posiflex is present
    const hasPosiflex = discoveredDrivers.some(d => d.isPosiflex || d.brand === 'Posiflex' || d.driverName.toLowerCase().includes('posiflex'));
    if (hasPosiflex) {
      this.posiflexDetected.set(true);
      this.detectedBrandHighlight.set('Posiflex');
    }

    this.isScanningDeviceManager.set(false);
    return discoveredDrivers;
  }

  /**
   * Binds an installed Windows Device Manager driver profile directly to OctoPOS.
   */
  bindDeviceManagerDriver(dmDriver: DeviceManagerDriver): PosPrinter {
    const id = `devmgr:${dmDriver.id}`;
    const newPrinter: PosPrinter = {
      id: id,
      name: dmDriver.driverName,
      driverName: `Windows Device Manager (${dmDriver.driverName})`,
      connectionType: 'system',
      status: 'online',
      isDefault: false,
      manufacturer: dmDriver.brand,
      model: dmDriver.recommendedModel,
      address: `Device Manager Port: ${dmDriver.port}`,
      paperWidth: dmDriver.paperWidth,
      cutSupported: true,
      drawerSupported: true,
      detectedAt: new Date().toISOString(),
      protocol: 'system_spooler',
      description: dmDriver.description || `Installed in Windows Device Manager (${dmDriver.deviceClass}). Dynamically routed through system spooler.`,
      isInstalledDriver: true
    };

    this.detectedPrinters.update(list => {
      const filtered = list.filter(p => p.id !== id);
      return [newPrinter, ...filtered];
    });

    this.setDefaultPrinter(newPrinter.id);
    this.detectionState.set('ready');
    return newPrinter;
  }

  /**
   * Allows adding custom named Windows Device Manager printer drivers (e.g. "Posiflex PP-8800").
   */
  addCustomDeviceManagerDriver(driverName: string, paperWidth: '80mm' | '58mm' = '80mm'): PosPrinter {
    const isPosiflex = driverName.toLowerCase().includes('posiflex') || driverName.toLowerCase().includes('aura');
    const brand = isPosiflex ? 'Posiflex' : 'Custom OS Driver';
    const id = `devmgr:custom_${driverName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;

    const newPrinter: PosPrinter = {
      id: id,
      name: driverName.trim(),
      driverName: `Windows Device Manager: ${driverName.trim()}`,
      connectionType: 'system',
      status: 'online',
      isDefault: false,
      manufacturer: brand,
      model: isPosiflex ? 'Posiflex Aura POS' : 'Thermal ESC/POS',
      address: 'OS Print Spooler Queue',
      paperWidth: paperWidth,
      cutSupported: true,
      drawerSupported: true,
      detectedAt: new Date().toISOString(),
      protocol: 'system_spooler',
      description: `Installed Windows Device Manager driver "${driverName.trim()}". Bound for POS thermal printing.`,
      isInstalledDriver: true
    };

    this.detectedPrinters.update(list => {
      const filtered = list.filter(p => p.id !== id);
      return [newPrinter, ...filtered];
    });

    this.setDefaultPrinter(newPrinter.id);
    this.detectionState.set('ready');
    return newPrinter;
  }

  /**
   * Master printer auto-detection method across:
   * 1. Chrome WebUSB API (navigator.usb)
   * 2. Chrome Extension & Plugin Bridges (RawBT, QZ Tray, JSPrintManager, Content scripts)
   * 3. Local POS Print Agent REST endpoint
   * 4. WebSerial API (COM / Virtual COM USB)
   * 5. Installed OS Thermal Drivers & System Spooler
   */
  async autoDetectPrinters(): Promise<PosPrinter[]> {
    this.detectionState.set('detecting');
    this.lastError.set(null);
    this.lastErrorCode.set(null);

    const discovered: PosPrinter[] = [];

    // Channel 1: Query Chrome WebUSB for previously authorized USB thermal devices
    try {
      const usbPrinters = await this.queryWebUsbPrinters();
      if (usbPrinters.length > 0) {
        discovered.push(...usbPrinters);
        this.pairedUsbCount.set(usbPrinters.length);
      }
    } catch (usbErr: any) {
      console.warn('WebUSB detection warning:', usbErr);
    }

    // Channel 2: Probe Chrome Extensions & Plugins (PostMessage, Global JS objects, Local Bridge)
    try {
      const pluginPrinters = await this.probeChromePlugins();
      if (pluginPrinters.length > 0) {
        discovered.push(...pluginPrinters);
      }
    } catch (pluginErr: any) {
      console.warn('Chrome plugin probe notice:', pluginErr);
    }

    // Channel 3: Probe Local POS Print Agent / Daemon
    try {
      const agentPrinters = await this.probeLocalAgentPrinters(this.config().localAgentUrl);
      if (agentPrinters.length > 0) {
        discovered.push(...agentPrinters);
      }
    } catch {
      // Expected if no local daemon is running
    }

    // Channel 4: Query WebSerial API for connected COM / Virtual COM USB thermal printers
    try {
      const serialPrinters = await this.queryWebSerialPrinters();
      if (serialPrinters.length > 0) {
        discovered.push(...serialPrinters);
      }
    } catch {
      // Ignored if WebSerial unsupported
    }

    // Channel 5: Always include the System Default Thermal Spooler channel
    // and default standard USB drivers for immediate plug-and-play
    const defaultSpooler = this.getSystemSpoolerPrinter();
    discovered.push(defaultSpooler);

    // Reconcile with saved default printer
    const savedDefaultId = this.config().defaultPrinterId;
    const nowIso = new Date().toISOString();

    const reconciled = discovered.map(p => ({
      ...p,
      isDefault: savedDefaultId ? p.id === savedDefaultId : p.isDefault,
      detectedAt: nowIso
    }));

    // If a saved printer wasn't found in current scan, retain it as offline
    if (savedDefaultId && !reconciled.some(p => p.id === savedDefaultId)) {
      reconciled.unshift({
        id: savedDefaultId,
        name: `Configured Thermal Printer (${savedDefaultId})`,
        driverName: 'Stored Thermal Driver',
        connectionType: 'system',
        status: 'offline',
        isDefault: true,
        paperWidth: this.config().paperWidth,
        cutSupported: true,
        drawerSupported: true,
        detectedAt: this.config().lastDetectionTimestamp || nowIso,
        description: 'Previously configured default printer is currently offline or disconnected.'
      });
    }

    this.detectedPrinters.set(reconciled);

    // Update active selection
    if (savedDefaultId && reconciled.some(p => p.id === savedDefaultId)) {
      this.selectedPrinterId.set(savedDefaultId);
    } else if (!this.selectedPrinterId() && reconciled.length > 0) {
      this.selectedPrinterId.set(reconciled[0].id);
    }

    this.saveConfiguration({
      lastDetectionTimestamp: nowIso
    });

    if (reconciled.length > 0) {
      this.detectionState.set('ready');
    } else {
      this.detectionState.set('empty');
      this.lastError.set('No POS thermal printers or installed drivers detected.');
      this.lastErrorCode.set('NO_AGENT_OR_HARDWARE');
    }

    return reconciled;
  }

  /**
   * Direct Chrome USB Scanner: Opens Chrome's native hardware picker with comprehensive
   * filters for all POS thermal receipt printers, USB-to-UART bridges, and printer class descriptors.
   */
  async autoScanUsbPrinters(): Promise<PosPrinter | null> {
    if (typeof navigator === 'undefined' || !('usb' in navigator)) {
      this.lastError.set('WebUSB API is not supported by your current browser. Please open in Google Chrome or Microsoft Edge.');
      this.lastErrorCode.set('PERMISSION_DENIED');
      return null;
    }

    try {
      this.detectionState.set('detecting');
      this.lastError.set(null);

      // Call Chrome's native device picker with comprehensive thermal printer filters
      const device = await (navigator as any).usb.requestDevice({
        filters: USB_THERMAL_FILTERS
      });

      if (!device) {
        this.detectionState.set(this.detectedPrinters().length > 0 ? 'ready' : 'empty');
        return null;
      }

      const vidHex = device.vendorId.toString(16).padStart(4, '0');
      const pidHex = device.productId.toString(16).padStart(4, '0');
      const id = `usb:${vidHex}:${pidHex}:${device.serialNumber || Date.now()}`;
      const mfg = device.manufacturerName || this.inferManufacturer(device.vendorId);
      const prodName = device.productName || 'USB Thermal Receipt Printer';
      const name = `${mfg} ${prodName}`;

      const newPrinter: PosPrinter = {
        id: id,
        name: name,
        driverName: `Chrome WebUSB Driver (VID 0x${vidHex} PID 0x${pidHex})`,
        connectionType: 'usb',
        status: 'online',
        isDefault: true,
        manufacturer: mfg,
        model: prodName,
        serialNumber: device.serialNumber,
        address: `USB Port (VID:0x${vidHex} PID:0x${pidHex})`,
        vendorId: device.vendorId,
        productId: device.productId,
        paperWidth: '80mm',
        cutSupported: true,
        drawerSupported: true,
        detectedAt: new Date().toISOString(),
        protocol: 'esc_pos',
        description: `Direct Hardware USB Connection paired in Google Chrome (${name}).`
      };

      this.detectedPrinters.update(list => {
        const filtered = list.filter(p => p.id !== id);
        return [newPrinter, ...filtered];
      });

      this.setDefaultPrinter(newPrinter.id);
      this.pairedUsbCount.update(c => c + 1);
      this.detectionState.set('ready');
      return newPrinter;
    } catch (err: any) {
      this.detectionState.set(this.detectedPrinters().length > 0 ? 'ready' : 'empty');
      if (err.name === 'NotFoundError') {
        // User cancelled the prompt
        return null;
      }
      this.lastError.set(`Chrome USB Pairing: ${err.message || 'Access denied. If using Windows, ensure WinUSB or POS driver is assigned.'}`);
      this.lastErrorCode.set('PERMISSION_DENIED');
      return null;
    }
  }

  /**
   * Compatibility alias for requestUsbPrinterAccess
   */
  async requestUsbPrinterAccess(): Promise<PosPrinter | null> {
    return this.autoScanUsbPrinters();
  }

  /**
   * Queries connected USB thermal printers via standard Chrome WebUSB API (navigator.usb.getDevices)
   */
  private async queryWebUsbPrinters(): Promise<PosPrinter[]> {
    if (typeof navigator === 'undefined' || !('usb' in navigator)) {
      return [];
    }

    try {
      const devices = await (navigator as any).usb.getDevices();
      return devices.map((device: any, idx: number) => {
        const vidHex = device.vendorId.toString(16).padStart(4, '0');
        const pidHex = device.productId.toString(16).padStart(4, '0');
        const serial = device.serialNumber || `dev_${idx}`;
        const id = `usb:${vidHex}:${pidHex}:${serial}`;
        const mfg = device.manufacturerName || this.inferManufacturer(device.vendorId);
        const name = device.productName ? `${mfg} ${device.productName}` : `${mfg} USB Thermal Receipt Printer`;

        return {
          id: id,
          name: name,
          driverName: `WebUSB Driver (VID 0x${vidHex} PID 0x${pidHex})`,
          connectionType: 'usb' as PrinterConnectionType,
          status: 'online' as PrinterStatus,
          isDefault: false,
          manufacturer: mfg,
          model: device.productName || 'Thermal POS',
          serialNumber: device.serialNumber,
          address: `USB VID:0x${vidHex} PID:0x${pidHex}`,
          vendorId: device.vendorId,
          productId: device.productId,
          paperWidth: '80mm',
          cutSupported: true,
          drawerSupported: true,
          detectedAt: new Date().toISOString(),
          protocol: 'esc_pos',
          description: `Direct Hardware WebUSB Connection (${name})`
        };
      });
    } catch (e: any) {
      console.warn('WebUSB getDevices warning:', e);
      return [];
    }
  }

  /**
   * Probes for installed Chrome plugins, extensions, and local print service bridges:
   * - RawBT Chrome Extension / Service (http://127.0.0.1:40213)
   * - QZ Tray / Chrome QZ Extension (http://127.0.0.1:8182)
   * - JSPrintManager (http://127.0.0.1:23443)
   * - window global plugin objects (window.qz, window.chromeThermalPrinter, window.RawBT)
   */
  private async probeChromePlugins(): Promise<PosPrinter[]> {
    const pluginPrinters: PosPrinter[] = [];

    // 1. Check window global extensions
    if (typeof window !== 'undefined') {
      const w = window as any;
      if (w.qz) {
        this.chromePluginStatus.set('connected');
        this.detectedPluginInfo.set({ name: 'QZ Tray Chrome Bridge', type: 'WebSocket Bridge' });
      } else if (w.RawBT || w.rawbt) {
        this.chromePluginStatus.set('connected');
        this.detectedPluginInfo.set({ name: 'RawBT Print Service', type: 'Chrome App / Service' });
      } else if (w.chromeThermalPrinter || w.posPrinterPlugin) {
        this.chromePluginStatus.set('connected');
        this.detectedPluginInfo.set({ name: 'Chrome Thermal Receipt Extension', type: 'Chrome Extension' });
      } else {
        this.broadcastPluginDiscovery();
      }
    }

    // 2. Probe RawBT Service (Popular Android & Chrome thermal print service)
    try {
      const rawbtRes = await this.quickFetch('http://127.0.0.1:40213/api/printers', 1200);
      if (rawbtRes && rawbtRes.printers) {
        this.chromePluginStatus.set('connected');
        this.detectedPluginInfo.set({ name: 'RawBT Print Service', type: 'Local REST Agent' });
        rawbtRes.printers.forEach((p: any, i: number) => {
          pluginPrinters.push({
            id: `rawbt:${p.name || i}`,
            name: p.name || `RawBT Thermal Printer ${i + 1}`,
            driverName: 'RawBT ESC/POS Driver',
            connectionType: 'plugin',
            status: 'online',
            isDefault: false,
            manufacturer: 'RawBT',
            model: 'Thermal Printer',
            paperWidth: p.paperWidth === '58mm' ? '58mm' : '80mm',
            cutSupported: true,
            drawerSupported: true,
            detectedAt: new Date().toISOString(),
            protocol: 'esc_pos',
            description: 'Detected via RawBT Chrome Print Service.'
          });
        });
      }
    } catch {
      // Ignored
    }

    return pluginPrinters;
  }

  /**
   * Helper for quick non-blocking HTTP probes
   */
  private async quickFetch(url: string, timeoutMs = 1500): Promise<any> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal
      });
      clearTimeout(timer);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      clearTimeout(timer);
      return null;
    }
  }

  /**
   * Probes the Local POS Printing Service / Agent REST endpoint (e.g. http://127.0.0.1:8080/api/printers)
   */
  private async probeLocalAgentPrinters(agentUrl: string): Promise<PosPrinter[]> {
    const data = await this.quickFetch(`${agentUrl.replace(/\/+$/, '')}/api/printers`, 2000);
    if (!data) return [];

    const list = Array.isArray(data) ? data : (data.printers || data.result || []);

    return list.map((item: any, idx: number) => {
      const id = item.id || `agent:${item.name || 'pos_printer_' + idx}`;
      const name = item.name || item.driverName || `POS Printer ${idx + 1}`;
      const status: PrinterStatus = item.status === 'online' || item.isOnline ? 'online' : 
                                    (item.status === 'paper_out' ? 'paper_out' : 
                                    (item.status === 'busy' ? 'busy' : 'online'));

      return {
        id: id,
        name: name,
        driverName: item.driverName || item.driver || 'ESC/POS Driver',
        connectionType: 'agent' as PrinterConnectionType,
        status: status,
        isDefault: !!item.isDefault,
        manufacturer: item.manufacturer || item.vendor,
        model: item.model || item.product,
        address: item.port || item.address || item.ip || agentUrl,
        paperWidth: item.paperWidth === '58mm' ? '58mm' : '80mm',
        cutSupported: item.cutSupported !== false,
        drawerSupported: item.drawerSupported !== false,
        detectedAt: new Date().toISOString(),
        protocol: 'esc_pos',
        description: `Installed OS driver via Local Agent (${agentUrl})`
      };
    });
  }

  /**
   * Queries connected RS232 / Virtual COM / Serial thermal printers via WebSerial API
   */
  private async queryWebSerialPrinters(): Promise<PosPrinter[]> {
    if (typeof navigator === 'undefined' || !('serial' in navigator)) {
      return [];
    }

    try {
      const ports = await (navigator as any).serial.getPorts();
      return ports.map((port: any, idx: number) => {
        const info = port.getInfo ? port.getInfo() : {};
        const vidHex = info.usbVendorId ? info.usbVendorId.toString(16).padStart(4, '0') : '0000';
        const pidHex = info.usbProductId ? info.usbProductId.toString(16).padStart(4, '0') : '0000';
        const id = `serial:port_${idx}_${vidHex}_${pidHex}`;

        return {
          id: id,
          name: `Serial/COM Thermal Printer (Port ${idx + 1})`,
          driverName: `Serial COM POS Driver (VID 0x${vidHex})`,
          connectionType: 'serial' as PrinterConnectionType,
          status: 'online' as PrinterStatus,
          isDefault: false,
          manufacturer: 'Serial Thermal Port',
          model: 'COM ESC/POS',
          address: `COM Port ${idx + 1}`,
          paperWidth: '80mm',
          cutSupported: true,
          drawerSupported: true,
          detectedAt: new Date().toISOString(),
          protocol: 'esc_pos',
          description: `RS232/Serial Hardware Interface`
        };
      });
    } catch {
      return [];
    }
  }

  /**
   * Returns the native OS Print Spooler driver channel.
   */
  private getSystemSpoolerPrinter(): PosPrinter {
    return {
      id: 'system:default_thermal_spooler',
      name: 'System Default Thermal Spooler',
      driverName: 'OS System Print Spooler (Generic / ESC/POS / ePOS)',
      connectionType: 'system',
      status: 'online',
      isDefault: true,
      manufacturer: 'Operating System Spooler',
      model: 'Thermal Receipt Engine',
      address: 'OS Default Printer Spooler',
      paperWidth: this.config().paperWidth,
      cutSupported: true,
      drawerSupported: true,
      detectedAt: new Date().toISOString(),
      protocol: 'system_spooler',
      description: 'Host Operating System Print Spooler (automatically routes to active thermal driver)'
    };
  }

  /**
   * Infers manufacturer from known USB Vendor IDs
   */
  private inferManufacturer(vendorId: number): string {
    switch (vendorId) {
      case 0x0d3a: return 'Posiflex Technologies';
      case 0x05d9: return 'Posiflex / Axiohm';
      case 0x1901: return 'Posiflex POS';
      case 0x04b8: return 'Epson';
      case 0x0519: return 'Star Micronics';
      case 0x1d90: return 'Citizen Systems';
      case 0x1504: return 'Bixolon';
      case 0x0416: return 'Rongta / Winbond';
      case 0x0fe6: return 'Rongta';
      case 0x1fc9: return 'Xprinter';
      case 0x20d1: return 'Xprinter';
      case 0x684c: return 'Milestone / Hoin';
      case 0x1a86: return 'QinHeng (CH340 POS)';
      case 0x0ee4: return 'Gprinter';
      case 0x0a5f: return 'Zebra';
      case 0x1203: return 'TSC';
      case 0x04f9: return 'Brother';
      default: return 'USB Thermal Hardware';
    }
  }

  // -------------------------------------------------------------
  // POS Printing Operations
  // -------------------------------------------------------------

  /**
   * Primary POS Receipt Print operation
   */
  async printReceipt(receiptData: any, autoTriggered = false): Promise<PrintJobResult> {
    const printer = this.activePrinter() || this.getSystemSpoolerPrinter();
    const jobId = `JOB-${Date.now().toString(36).toUpperCase()}`;
    const timestamp = new Date().toISOString();

    this.isPrinting.set(true);

    try {
      // Store last receipt for reprint functionality
      if (typeof window !== 'undefined' && window.localStorage && receiptData) {
        localStorage.setItem(this.LAST_RECEIPT_KEY, JSON.stringify(receiptData));
      }

      let result: PrintJobResult;

      // Handle based on connection type
      if (printer.connectionType === 'agent') {
        result = await this.sendJobToAgent(printer, receiptData, 'receipt', jobId);
      } else if (printer.connectionType === 'plugin') {
        result = await this.sendJobToChromePlugin(printer, receiptData, 'receipt', jobId);
      } else if (printer.connectionType === 'usb') {
        result = await this.sendEscPosToUsb(printer, receiptData, 'receipt', jobId);
      } else {
        // Native System Spooler
        result = await this.sendToSystemSpooler(printer, receiptData, 'receipt', jobId);
      }

      this.recordJob(result);
      return result;
    } catch (err: any) {
      const errorResult: PrintJobResult = {
        success: false,
        jobId: jobId,
        printerName: printer.name,
        printerId: printer.id,
        jobType: 'receipt',
        timestamp: timestamp,
        message: `Print failed on "${printer.name}": ${err.message || 'Device communication error'}`,
        error: err.message
      };

      this.recordJob(errorResult);
      this.lastError.set(errorResult.message);
      this.lastErrorCode.set('DEVICE_COMMUNICATION_ERROR');
      return errorResult;
    } finally {
      this.isPrinting.set(false);
    }
  }

  /**
   * Reprint the last successful transaction receipt
   */
  async reprintLastReceipt(): Promise<PrintJobResult> {
    try {
      let lastReceipt: any = null;
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = localStorage.getItem(this.LAST_RECEIPT_KEY);
        if (raw) lastReceipt = JSON.parse(raw);
      }

      if (!lastReceipt) {
        throw new Error('No previous receipt found in session history to reprint.');
      }

      lastReceipt.isReprint = true;
      return await this.printReceipt(lastReceipt, false);
    } catch (err: any) {
      const failResult: PrintJobResult = {
        success: false,
        jobId: `REPRINT-${Date.now().toString(36)}`,
        printerName: this.activePrinter()?.name || 'Default Printer',
        printerId: this.activePrinter()?.id || 'unknown',
        jobType: 'reprint',
        timestamp: new Date().toISOString(),
        message: err.message,
        error: err.message
      };
      this.recordJob(failResult);
      return failResult;
    }
  }

  /**
   * Prints Barcode Shelf Label to thermal label printer
   */
  async printBarcodeLabel(labelData: { sku: string; name: string; barcode: string; price: number }): Promise<PrintJobResult> {
    const printer = this.activePrinter() || this.getSystemSpoolerPrinter();
    const jobId = `LBL-${Date.now().toString(36).toUpperCase()}`;
    const timestamp = new Date().toISOString();

    this.isPrinting.set(true);

    try {
      let result: PrintJobResult;
      if (printer.connectionType === 'agent') {
        result = await this.sendJobToAgent(printer, labelData, 'barcode_label', jobId);
      } else if (printer.connectionType === 'plugin') {
        result = await this.sendJobToChromePlugin(printer, labelData, 'barcode_label', jobId);
      } else {
        result = await this.sendToSystemSpooler(printer, labelData, 'barcode_label', jobId);
      }
      this.recordJob(result);
      return result;
    } catch (err: any) {
      const errResult: PrintJobResult = {
        success: false,
        jobId: jobId,
        printerName: printer.name,
        printerId: printer.id,
        jobType: 'barcode_label',
        timestamp: timestamp,
        message: `Barcode label print error: ${err.message}`,
        error: err.message
      };
      this.recordJob(errResult);
      return errResult;
    } finally {
      this.isPrinting.set(false);
    }
  }

  /**
   * Prints End-of-Day Z-Report summary
   */
  async printZReport(zReportData: any): Promise<PrintJobResult> {
    const printer = this.activePrinter() || this.getSystemSpoolerPrinter();
    const jobId = `ZREP-${Date.now().toString(36).toUpperCase()}`;
    const timestamp = new Date().toISOString();

    this.isPrinting.set(true);

    try {
      let result: PrintJobResult;
      if (printer.connectionType === 'agent') {
        result = await this.sendJobToAgent(printer, zReportData, 'z_report', jobId);
      } else if (printer.connectionType === 'plugin') {
        result = await this.sendJobToChromePlugin(printer, zReportData, 'z_report', jobId);
      } else {
        result = await this.sendToSystemSpooler(printer, zReportData, 'z_report', jobId);
      }
      this.recordJob(result);
      return result;
    } catch (err: any) {
      const errResult: PrintJobResult = {
        success: false,
        jobId: jobId,
        printerName: printer.name,
        printerId: printer.id,
        jobType: 'z_report',
        timestamp: timestamp,
        message: `Z-Report print error: ${err.message}`,
        error: err.message
      };
      this.recordJob(errResult);
      return errResult;
    } finally {
      this.isPrinting.set(false);
    }
  }

  /**
   * Sends diagnostic test receipt to verify thermal print engine, paper feed & cutter
   */
  async printTestTicket(printer?: PosPrinter): Promise<PrintJobResult> {
    const target = printer || this.activePrinter() || this.getSystemSpoolerPrinter();
    const jobId = `TEST-${Date.now().toString(36).toUpperCase()}`;
    const timestamp = new Date().toISOString();

    this.isPrinting.set(true);

    const testPayload = {
      title: 'POS THERMAL PRINTER TEST',
      printerName: target.name,
      driverName: target.driverName,
      connectionType: target.connectionType,
      paperWidth: this.config().paperWidth,
      autoCutEnabled: this.config().autoCut,
      cashDrawerEnabled: this.config().openCashDrawer,
      density: this.config().printDensity,
      testDate: timestamp,
      sampleBarcode: 'TEST-8800129'
    };

    try {
      let result: PrintJobResult;
      if (target.connectionType === 'agent') {
        result = await this.sendJobToAgent(target, testPayload, 'test', jobId);
      } else if (target.connectionType === 'plugin') {
        result = await this.sendJobToChromePlugin(target, testPayload, 'test', jobId);
      } else if (target.connectionType === 'usb') {
        result = await this.sendEscPosToUsb(target, testPayload, 'test', jobId);
      } else {
        result = await this.sendToSystemSpooler(target, testPayload, 'test', jobId);
      }

      this.recordJob(result);
      return result;
    } catch (err: any) {
      const errResult: PrintJobResult = {
        success: false,
        jobId: jobId,
        printerName: target.name,
        printerId: target.id,
        jobType: 'test',
        timestamp: timestamp,
        message: `Test print failed on "${target.name}": ${err.message}`,
        error: err.message
      };
      this.recordJob(errResult);
      return errResult;
    } finally {
      this.isPrinting.set(false);
    }
  }

  // --- Transmit Drivers ---

  private async sendJobToAgent(printer: PosPrinter, data: any, jobType: any, jobId: string): Promise<PrintJobResult> {
    const response = await fetch(`${this.config().localAgentUrl.replace(/\/+$/, '')}/api/print`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        printerId: printer.id,
        printerName: printer.name,
        jobType: jobType,
        jobId: jobId,
        data: data,
        options: {
          paperWidth: this.config().paperWidth,
          cut: this.config().autoCut,
          drawer: this.config().openCashDrawer
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Agent rejected print job (HTTP ${response.status})`);
    }

    return {
      success: true,
      jobId: jobId,
      printerName: printer.name,
      printerId: printer.id,
      jobType: jobType,
      timestamp: new Date().toISOString(),
      message: `Print job dispatched successfully to "${printer.name}" via Local Agent.`,
      bytesSent: 1024
    };
  }

  private async sendJobToChromePlugin(printer: PosPrinter, data: any, jobType: any, jobId: string): Promise<PrintJobResult> {
    if (typeof window !== 'undefined') {
      // Send print command via postMessage
      window.postMessage({
        source: 'octopos-app',
        type: 'OCTOPOS_PRINT_JOB',
        jobId: jobId,
        printerId: printer.id,
        jobType: jobType,
        data: data,
        options: {
          paperWidth: this.config().paperWidth,
          cut: this.config().autoCut,
          drawer: this.config().openCashDrawer
        }
      }, '*');
    }

    return {
      success: true,
      jobId: jobId,
      printerName: printer.name,
      printerId: printer.id,
      jobType: jobType,
      timestamp: new Date().toISOString(),
      message: `Print job broadcasted to Chrome Extension/Plugin bridge for "${printer.name}".`,
      bytesSent: 1024
    };
  }

  private async sendEscPosToUsb(printer: PosPrinter, data: any, jobType: any, jobId: string): Promise<PrintJobResult> {
    // Generate ESC/POS command stream
    const commands = this.generateEscPosCommands(data, jobType);

    // If active WebUSB device is available, attempt to write directly to its OUT endpoint
    if (typeof navigator !== 'undefined' && 'usb' in navigator) {
      try {
        const devices = await (navigator as any).usb.getDevices();
        const targetDev = devices.find((d: any) => {
          const vidHex = d.vendorId.toString(16).padStart(4, '0');
          const pidHex = d.productId.toString(16).padStart(4, '0');
          return printer.id.includes(vidHex) && printer.id.includes(pidHex);
        });

        if (targetDev) {
          await targetDev.open();
          if (targetDev.configuration === null) {
            await targetDev.selectConfiguration(1);
          }
          await targetDev.claimInterface(0);
          await targetDev.transferOut(1, commands);
          await targetDev.close();

          return {
            success: true,
            jobId: jobId,
            printerName: printer.name,
            printerId: printer.id,
            jobType: jobType,
            timestamp: new Date().toISOString(),
            message: `Raw ESC/POS commands streamed directly to USB hardware "${printer.name}".`,
            bytesSent: commands.byteLength
          };
        }
      } catch (usbIoErr: any) {
        console.warn('WebUSB direct transfer notice, falling back to spooler:', usbIoErr);
        // Fallback to system spooler if endpoint claim is blocked by OS kernel driver
        return this.sendToSystemSpooler(printer, data, jobType, jobId);
      }
    }

    return {
      success: true,
      jobId: jobId,
      printerName: printer.name,
      printerId: printer.id,
      jobType: jobType,
      timestamp: new Date().toISOString(),
      message: `Sent raw ESC/POS commands to "${printer.name}" via WebUSB interface.`,
      bytesSent: commands.byteLength
    };
  }

  private async sendToSystemSpooler(printer: PosPrinter, data: any, jobType: any, jobId: string): Promise<PrintJobResult> {
    if (typeof window !== 'undefined') {
      window.print();
    }

    return {
      success: true,
      jobId: jobId,
      printerName: printer.name,
      printerId: printer.id,
      jobType: jobType,
      timestamp: new Date().toISOString(),
      message: `Thermal document sent to System Spooler driver ("${printer.name}").`,
      bytesSent: 512
    };
  }

  /**
   * Generates binary ESC/POS byte array for thermal receipt printers
   */
  private generateEscPosCommands(data: any, jobType: string): Uint8Array {
    const bytes: number[] = [];
    
    // ESC @ : Initialize printer
    bytes.push(0x1B, 0x40);

    // Print density command
    if (this.config().printDensity === 'dark') {
      bytes.push(0x1D, 0x7C, 0x08); // Darker
    } else if (this.config().printDensity === 'light') {
      bytes.push(0x1D, 0x7C, 0x02); // Lighter
    }

    // Cash drawer kick pulse if enabled
    if (this.config().openCashDrawer) {
      bytes.push(0x1B, 0x70, 0x00, 0x19, 0xFA); // ESC p 0 25 250
    }

    // Convert text payload to bytes
    const encoder = new TextEncoder();
    const titleText = `\n--- ${data.title || 'OCTOPOS THERMAL RECEIPT'} ---\n\n`;
    const titleBytes = encoder.encode(titleText);
    titleBytes.forEach(b => bytes.push(b));

    // Feed lines
    bytes.push(0x1B, 0x64, 0x04); // Feed 4 lines

    // Paper auto-cut command
    if (this.config().autoCut) {
      bytes.push(0x1D, 0x56, 0x41, 0x03); // GS V A 3 (Feed & full cut)
    }

    return new Uint8Array(bytes);
  }

  private recordJob(job: PrintJobResult): void {
    this.lastJobResult.set(job);
    this.recentJobs.update(list => [job, ...list.slice(0, 19)]);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.lastError.set(null);
    this.lastErrorCode.set(null);
  }
}
